import { get } from "@/lib/http";
import { Paginated } from "@/types/api/pagination.type";
import {
  Plan,
  PlanActionsEnum,
  PlansWithSubscription,
  PlanWithsubscription
} from "@/types/plan.type";
import { toast } from "sonner";
import { proxy, snapshot, useSnapshot } from "valtio";
import { getAccountProxy } from "./account.store";
import { getSubscriptionProxy } from "./subscriptions.store";

const subscriptionProxy = getSubscriptionProxy();
const accountProxy = getAccountProxy();

export const planProxy = proxy({
  plans: {
    data: [],
    metadata: {
      currentPage: 0,
      count: 0,
      nextPage: 0,
      prevPage: 0,
      totalPages: 0
    }
  } as Paginated<PlansWithSubscription>,
  currentPlan: undefined as Plan | undefined,
  currentPlanLoading: true,
  isLoading: false,
  plansPageSize: 20,
  callbackAction: window.sessionStorage.getItem(
    "stripeCallbackAction"
  ) as PlanActionsEnum,

  fetchPlans: async (page: number, perPage: number = 5) => {
    planProxy.isLoading = true;
    try {
      const response = await get<Paginated<PlansWithSubscription>>(
        `/api/plan/?page=${page}&userId=${accountProxy.userInfo?.profile["urn:zitadel:iam:user:resourceowner:id"]}&per_page=${perPage}`
      );

      planProxy.plans = {
        data: response.data,
        metadata: response.metadata
      };
    } catch (error) {
      console.log("Error fetching plans:", error);
      toast.error(`Error fetching plans : ${error}`);
    } finally {
      planProxy.isLoading = false;
    }
  },

  getPlan: async (code: string, external_customer_id: string | unknown) => {
    planProxy.currentPlanLoading = true;

    const foundPlan = planProxy.plans?.data.find(
      (plan: PlanWithsubscription) => plan.plan.code === code
    );

    try {
      if (foundPlan) {
        planProxy.currentPlan = foundPlan.plan;
      } else {
        const response = await get<Plan>(`/api/plan/${code}`);
        planProxy.currentPlan = response;
      }

      if (external_customer_id) {
        subscriptionProxy.exists(
          planProxy.currentPlan?.code,
          external_customer_id
        );
      }
    } catch (error) {
      console.error("Error fetching plan:", error);
      toast.error(`Error fetching plan: ${error}`);
      planProxy.currentPlan = undefined;
    } finally {
      planProxy.currentPlanLoading = false;
    }

    return planProxy.currentPlan;
  },

  handleCallbackAction: async () => {
    switch (planProxy.callbackAction) {
      case PlanActionsEnum.enum.subscribe: {
        window.sessionStorage.removeItem("stripeCallbackAction");
        const paymentMethods = await accountProxy.getPaymentMethods();

        if (paymentMethods.length == 0) {
          toast.error("Failed to get paymentMethod");
          // Don't redirect back to stripe to avoid loop
          return;
        }

        subscriptionProxy.subscribe(
          planProxy.currentPlan?.code,
          accountProxy.userInfo?.profile[
            "urn:zitadel:iam:user:resourceowner:id"
          ]
        );
        return;
      }

      default:
        return;
    }
  }
});

export const getPlanSnapshot = () => snapshot(planProxy);
export const getPlanProxy = () => planProxy;

export const usePlanSnapshot = (options?: Parameters<typeof useSnapshot>[1]) =>
  useSnapshot(planProxy, options);

export const usePlanProxy = () => planProxy;

export const usePlanStore = (options?: Parameters<typeof useSnapshot>[1]) => {
  return {
    planSnapshot: useSnapshot(planProxy, options),
    planProxy: planProxy
  };
};
